import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { ErrorMessage } from "@components/donate/DonateV3/PaymentProcess/pages/Donate";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import { DonateFormType } from "@components/donate/DonateV3/types";
import { useFundraiserForNonprofits } from "@components/donate/hooks";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Big } from "big.js";
import React, { Fragment } from "react";
import { UseFormReturn } from "react-hook-form";

import { FundraiserResponse } from "@every.org/common/src/codecs/entities";
import { TextSize } from "@every.org/common/src/display/text";
import { FGC_CAMPAIGN_CODE } from "@every.org/common/src/entity/constants";
import {
  DonationFrequencyToUserFacingTextMap,
  DonationFrequency,
  DonationFlowPaymentOption,
  FundraiserType,
  lowercaseUserFacingFrequency,
} from "@every.org/common/src/entity/types";
import { MAX_CRYPTO_DECIMALS_FOR_DISPLAY } from "@every.org/common/src/helpers/cryptoCurrency";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import { MediaSize, cssForMediaSize } from "src/theme/mediaQueries";
import {
  horizontalStackCss,
  verticalStackCss,
  spacing,
} from "src/theme/spacing";
import { textSizeCss, FontWeight } from "src/theme/text";
import { useUserLayer } from "src/utility/abtesting";
import {
  displayCryptoValueInUserLocale,
  displayCurrencyValueInUserLocale,
} from "src/utility/currency";

const TipSummaryCopyExperiment = () => {
  const summary = useUserLayer("tip_copy_layer", false).get(
    "summary",
    "Donation for Every.org"
  );
  return <div>{summary}</div>;
};

const LegendTitle = styled.legend`
  color: var(${colorCssVars.text.body});
  font-weight: ${FontWeight.BOLD};
  ${textSizeCss[TextSize.s]};
`;

const SummaryWrapper = styled.div`
  color: var(${colorCssVars.text.secondary});
  ${textSizeCss[TextSize.s]};
  ${verticalStackCss.xs};
`;

const TipRow = styled.div<{ isCryptoDonation: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.xs};

  ${({ isCryptoDonation }) =>
    isCryptoDonation &&
    cssForMediaSize({
      max: MediaSize.SMALL,
      css: css`
        flex-direction: column;
        align-items: flex-start;
        gap: 0;
      `,
    })}
`;

const TipDivider = styled.div<{ isCryptoDonation: boolean }>`
  border-left: 1px solid #dadddd;
  height: 100%;

  ${({ isCryptoDonation }) =>
    isCryptoDonation &&
    cssForMediaSize({
      max: MediaSize.SMALL,
      css: css`
        display: none;
      `,
    })}
`;

const SummaryList = styled.ul`
  li {
    ${horizontalStackCss.none};
    justify-content: space-between;
    padding: ${spacing.s} ${spacing.none};
    box-shadow: 0px 1px 0px #eaeded;
    > span {
      color: var(${colorCssVars.text.body});
    }

    button {
      color: var(${colorCssVars.text.secondary}) !important;
      &:hover {
        text-decoration: underline;
      }
    }

    strong {
      color: var(${colorCssVars.accent.large});
    }
  }
`;

export interface DonationSummaryProps {
  form: UseFormReturn<DonateFormType>;
  formContext: DonateFormContext;
  nonprofit: ContextNonprofit;
  fundraiser?: FundraiserResponse | null;
  paymentOption: DonationFlowPaymentOption;
  onTipEdit?: () => void;
  coverCostsMode?: boolean;
}

export const DonationSummary = ({
  form,
  formContext,
  nonprofit,
  fundraiser,
  paymentOption,
  onTipEdit,
  coverCostsMode = false,
}: DonationSummaryProps) => {
  const {
    creditAmount,
    amountBig: donationAmount,
    matchingAmount,
  } = formContext;
  const { watch } = form;

  const frequency = watch("frequency");
  const currency = watch("currency");
  const tipAmount = watch("amountToEveryOrg") || 0;
  const coverCostsChecked = watch("coverCostsChecked") ?? true;
  const coverCostsAmount = watch("coverCostsAmount") || "0";
  const cryptoCurrency = watch("cryptoCurrency");
  const cryptoPledgeAmount = watch("cryptoPledgeAmount");
  const errorMessage = form.formState.errors.amount?.message;
  const cryptoPledgeAmountTotal = formContext.totalCryptoAmount;
  const totalStockAmount = formContext.totalStockAmount;
  const totalGift = donationAmount.add(matchingAmount);
  const coverCostsAmountBig =
    coverCostsMode && coverCostsChecked
      ? new Big(coverCostsAmount)
      : new Big(0);
  const contributionAmount = coverCostsMode
    ? coverCostsAmountBig
    : new Big(tipAmount);
  const totalAmount = donationAmount
    .add(contributionAmount)
    .minus(creditAmount);
  const stockAmount = watch("stockAmount");
  const stockSymbol = watch("stockSymbol");

  const isCryptoDonation = paymentOption === DonationFlowPaymentOption.CRYPTO;
  const isDAFDonation = paymentOption === DonationFlowPaymentOption.DAF;
  const isIRADonation = paymentOption === DonationFlowPaymentOption.IRA;
  const isStockDonation = paymentOption === DonationFlowPaymentOption.STOCKS;

  const isMultiNonprofitFundraiser = Boolean(
    fundraiser && fundraiser.type === FundraiserType.MULTIPLE_NONPROFITS
  );

  const fundraiserForNonprofits = useFundraiserForNonprofits(fundraiser);
  const toNonprofits = watch("toNonprofits") || [];
  const toNonprofitWeights = watch("toNonprofitWeights") || [];
  const matchingWeights = watch("toNonprofitMatchings");

  if (coverCostsMode && !matchingAmount.gt(0) && !creditAmount.gt(0)) {
    return null;
  }

  const splitAmounts = isMultiNonprofitFundraiser
    ? nonprofitsToSplitAmounts({
        fundraiserNonprofits: fundraiserForNonprofits,
        toNonprofits,
        toNonprofitWeights,
        matchingWeights,
        amount: isStockDonation
          ? new Big(stockAmount ?? 0)
          : isCryptoDonation
          ? new Big(cryptoPledgeAmount ?? 0)
          : donationAmount,
      })
    : undefined;

  const renderAmountRow = (
    nonprofit: { id: string; name: string },
    amount: Big,
    match?: Big
  ) => (
    <Fragment key={nonprofit.id}>
      <li>
        <div>Donation for {nonprofit.name}</div>
        {cryptoCurrency && cryptoPledgeAmount && isCryptoDonation ? (
          <span>
            {displayCryptoValueInUserLocale({
              cryptoCurrency: cryptoCurrency,
              amount,
              maximumSignificantDigits: MAX_CRYPTO_DECIMALS_FOR_DISPLAY,
            })}
          </span>
        ) : (
          <span>
            {displayCurrencyValueInUserLocale({
              currencyValue: {
                currency,
                amount,
              },
            })}
          </span>
        )}
      </li>
      {match && match.gt(0) && (
        <li>
          <div>
            Match for {nonprofit.name}
            {frequency !== DonationFrequency.ONCE ? "*" : ""}
          </div>
          <span>
            <strong>+</strong>&nbsp;
            {displayCurrencyValueInUserLocale({
              currencyValue: {
                currency,
                amount: match,
              },
            })}
          </span>
        </li>
      )}
    </Fragment>
  );

  if (isStockDonation) {
    return (
      <SummaryWrapper>
        <LegendTitle>Your donation</LegendTitle>
        <SummaryList>
          <li>
            <div>Stock symbol</div>
            <span>{stockSymbol}</span>
          </li>
          {splitAmounts && splitAmounts.length > 0 ? (
            splitAmounts.map(({ nonprofit, amount }) => (
              <li key={nonprofit.id}>
                <div>Number of shares for {nonprofit.name}</div>
                <span>{amount.toString()}</span>
              </li>
            ))
          ) : (
            <li>
              <div>Number of shares</div>
              <span>{stockAmount}</span>
            </li>
          )}
          <li>
            <div css={horizontalStackCss.xs}>
              <span>Donation for Every.org (Optional)</span>
              {onTipEdit && (
                <React.Fragment>
                  <TipDivider isCryptoDonation={isCryptoDonation} />
                  <Button
                    data-tname="donationSummaryTipEdit"
                    role={ButtonRole.TEXT_SECONDARY}
                    onClick={{
                      kind: ButtonTargetKind.FUNCTION,
                      action: onTipEdit,
                    }}
                  >
                    Edit
                  </Button>
                </React.Fragment>
              )}
            </div>
            <span>{tipAmount}</span>
          </li>
          <li>
            <div>Total</div>
            <span>{totalStockAmount?.toString()}</span>
          </li>
        </SummaryList>
      </SummaryWrapper>
    );
  }

  return (
    <SummaryWrapper>
      <LegendTitle>Your donation</LegendTitle>
      <SummaryList>
        {!isCryptoDonation && !isIRADonation && (
          <li>
            <div>Frequency</div>
            <span>{DonationFrequencyToUserFacingTextMap[frequency]}</span>
          </li>
        )}
        {renderAmountRow(
          toNonprofits.length && fundraiser
            ? {
                id: fundraiser.id,
                name: fundraiser.title,
              }
            : nonprofit,
          cryptoCurrency && cryptoPledgeAmount && isCryptoDonation
            ? new Big(cryptoPledgeAmount)
            : new Big(donationAmount)
        )}
        {(!splitAmounts || splitAmounts.length === 0) &&
          matchingAmount.gt(0) && (
            <li>
              <div>
                Match amount{frequency !== DonationFrequency.ONCE ? "*" : ""}
              </div>
              <span>
                <strong>+</strong>&nbsp;
                {displayCurrencyValueInUserLocale({
                  currencyValue: {
                    currency,
                    amount: matchingAmount,
                  },
                })}
              </span>
            </li>
          )}
        {matchingAmount.gt(0) && (
          <li>
            <div>Total gift</div>
            <span>
              {displayCurrencyValueInUserLocale({
                currencyValue: {
                  currency,
                  amount: new Big(totalGift),
                },
              })}
            </span>
          </li>
        )}
        {formContext.enableTipping && !coverCostsMode && (
          <li>
            <TipRow isCryptoDonation={isCryptoDonation}>
              <TipSummaryCopyExperiment />
              {onTipEdit && (
                <React.Fragment>
                  <TipDivider isCryptoDonation={isCryptoDonation} />
                  <Button
                    data-tname="donationSummaryTipEdit"
                    role={ButtonRole.TEXT_SECONDARY}
                    onClick={{
                      kind: ButtonTargetKind.FUNCTION,
                      action: onTipEdit,
                    }}
                  >
                    Edit
                  </Button>
                </React.Fragment>
              )}
            </TipRow>
            <span>
              {isCryptoDonation && cryptoCurrency
                ? displayCryptoValueInUserLocale({
                    cryptoCurrency: cryptoCurrency,
                    amount: new Big(tipAmount),
                    maximumSignificantDigits: MAX_CRYPTO_DECIMALS_FOR_DISPLAY,
                  })
                : displayCurrencyValueInUserLocale({
                    currencyValue: {
                      currency,
                      amount: new Big(tipAmount),
                    },
                  })}
            </span>
          </li>
        )}
        {coverCostsMode && contributionAmount.gt(0) && (
          <li>
            <div>Costs covered</div>
            <span>
              {displayCurrencyValueInUserLocale({
                currencyValue: {
                  currency,
                  amount: contributionAmount,
                },
              })}
            </span>
          </li>
        )}
        {!isDAFDonation && !isIRADonation && creditAmount.gt(0) && (
          <li>
            <div>Donation credits used</div>
            <span>
              {displayCurrencyValueInUserLocale({
                currencyValue: {
                  currency,
                  amount: creditAmount,
                },
              })}
            </span>
          </li>
        )}
        {(formContext.enableTipping || coverCostsMode) && (
          <li>
            <div>
              {isCryptoDonation
                ? "Total"
                : frequency === DonationFrequency.ONCE
                ? "Total charge"
                : `Total ${lowercaseUserFacingFrequency(frequency)} charge ${
                    creditAmount.gt(0)
                      ? matchingAmount.gt(0)
                        ? "**"
                        : "*"
                      : ""
                  }`}
            </div>
            <span>
              {isCryptoDonation && cryptoCurrency && cryptoPledgeAmountTotal
                ? displayCryptoValueInUserLocale({
                    cryptoCurrency: cryptoCurrency,
                    amount: cryptoPledgeAmountTotal,
                    maximumSignificantDigits: MAX_CRYPTO_DECIMALS_FOR_DISPLAY,
                  })
                : displayCurrencyValueInUserLocale({
                    currencyValue: {
                      currency,
                      amount: totalAmount,
                    },
                  })}
            </span>
          </li>
        )}
        {/* {form.getValues().lastName && form.getValues().firstName && (
          <li>
            <div>Dedicated to</div>
            <span>{`${form.getValues().firstName} ${
              form.getValues().lastName
            }`}</span>
          </li>
        )} */}
      </SummaryList>
      {frequency !== DonationFrequency.ONCE && (
        <React.Fragment>
          {matchingAmount.gt(0) && (
            <p>
              * {DonationFrequencyToUserFacingTextMap[frequency]} gifts will
              only be matched for the first{" "}
              {formContext.nonprofitMatchCampaign?.code === FGC_CAMPAIGN_CODE
                ? "two months"
                : "month"}
              .
            </p>
          )}
          {creditAmount.gt(0) && (
            <p>
              {matchingAmount.gt(0) ? "**" : "*"} The monthly charge when no
              donation credits are available will be{" "}
              {displayCurrencyValueInUserLocale({
                currencyValue: {
                  currency,
                  amount: totalAmount.plus(creditAmount),
                },
              })}
            </p>
          )}
        </React.Fragment>
      )}
      {errorMessage && (
        <div css={horizontalStackCss.xs}>
          <Icon
            iconImport={() => import("@components/Icon/icons/AlertIcon")}
            size={IconSize.MEDIUM}
            display={IconDisplay.ERROR}
          />
          <div css={verticalStackCss.xxxs}>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          </div>
        </div>
      )}
    </SummaryWrapper>
  );
};

function nonprofitsToSplitAmounts({
  fundraiserNonprofits,
  toNonprofits,
  toNonprofitWeights,
  matchingWeights,
  amount,
}: {
  fundraiserNonprofits: ContextNonprofit[];
  toNonprofits: string[];
  toNonprofitWeights: number[];
  matchingWeights?: number[];
  amount: Big;
}) {
  return toNonprofits
    .map((nonprofitId, index) => {
      const splitAmount = new Big(toNonprofitWeights[index])
        .div(100)
        .times(amount);
      const splitMatch =
        matchingWeights && new Big(matchingWeights[index]).div(100);

      const nonprofit = fundraiserNonprofits.find((n) => n.id === nonprofitId);
      if (nonprofit) {
        return {
          nonprofit,
          amount: splitAmount,
          match: splitMatch,
        };
      }
      return undefined;
    })
    .filter(
      (
        x
      ): x is {
        nonprofit: ContextNonprofit;
        amount: Big;
        match: Big | undefined;
      } => x !== undefined
    );
}
